

import { fetchManagedUsers, fetchAbnStatuses, scanVevo } from "./api.js";

/* ---------- generic cache slot ---------- */
const makeSlot = () => ({ value: null, inflight: null });

const loadSlot = (slot, fetcher, force) => {
  if (slot.value && !force) return Promise.resolve(slot.value);
  if (slot.inflight && !force) return slot.inflight;
  slot.inflight = fetcher()
    .then((val) => {
      slot.value = val;
      slot.inflight = null;
      return val;
    })
    .catch((err) => {
      slot.inflight = null;
      if (slot.value) return slot.value; // keep stale data over nothing
      throw err;
    });
  return slot.inflight;
};

/* ---------- managed users ---------- */
const usersSlot = makeSlot();

export const loadManagedUsers = (force = false) =>
  loadSlot(
    usersSlot,
    () =>
      fetchManagedUsers().then((res) => {
        if (res.status !== "success") throw new Error(res.error || "Failed to load users");
        return res.data ?? [];
      }),
    force
  );

export const peekManagedUsers = () => usersSlot.value;

// Keep the cache in step with edits made on the page (auto-save, uploads),
// so navigating away and back shows the edited values, not the originals.
export const syncManagedUsers = (records) => {
  if (Array.isArray(records)) usersSlot.value = records;
};

/* ---------- ABN statuses ---------- */
const abnSlot = makeSlot();

const abnPayload = (records) =>
  (records || [])
    .filter((r) => r.workstatus === "ABN" && r.abnNumber)
    .map((r) => ({ recordId: r.recordId, abn: r.abnNumber }));

const toMap = (res, label) => {
  if (res.status !== "success") throw new Error(res.error || label);
  const map = {};
  (res.results ?? []).forEach((row) => {
    if (row.recordId) map[row.recordId] = row;
  });
  return map;
};

export const loadAbnStatusMap = (records, force = false) => {
  const payload = abnPayload(records);
  if (payload.length === 0) return Promise.resolve(abnSlot.value ?? {});

  // Warm cache but the record list has grown since (revalidate picked up a
  // new contractor): look up only the missing ABNs and merge them in.
  if (abnSlot.value && !force && !abnSlot.inflight) {
    const missing = payload.filter((p) => !abnSlot.value[p.recordId]);
    if (missing.length === 0) return Promise.resolve(abnSlot.value);
    abnSlot.inflight = fetchAbnStatuses(missing, false)
      .then((res) => {
        abnSlot.value = { ...abnSlot.value, ...toMap(res, "ABN lookup failed") };
        abnSlot.inflight = null;
        return abnSlot.value;
      })
      .catch(() => {
        abnSlot.inflight = null;
        return abnSlot.value;
      });
    return abnSlot.inflight;
  }

  return loadSlot(
    abnSlot,
    () => fetchAbnStatuses(payload, force).then((res) => toMap(res, "ABN lookup failed")),
    force
  );
};

export const peekAbnStatusMap = () => abnSlot.value;

/* ---------- VEVO statuses ---------- */
const vevoSlot = makeSlot();

export const loadVevoStatusMap = (force = false) =>
  loadSlot(
    vevoSlot,
    () =>
      scanVevo([], force).then((res) => toMap(res, "VEVO scan failed")),
    force
  );

export const peekVevoStatusMap = () => vevoSlot.value;